import MuteButton from "@assets/actions/Mute.png";
import PlayIcon from "@assets/actions/play-icon.png";
import UnMuteButton from "@assets/actions/Sound.png";
import { QnBrightcoveVideo } from "@blocks/BrightcoveVideo/QnBrightcoveVideo";
import ShareSocialButton from "@blocks/ShareSocial/ShareSocialButton";
import { QN_SOCIAL_ICON_LIST } from "@config/QuickNewsConstants";
import Image from "@elements/ResponsiveImage/Image";
import Heading from "@elements/Typography/Heading";
import { BC_PLAYER_ID_QN } from "@helper/getEnvVariables";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type PlayerProps = {
  bcVideo: LatestVideoResponse;
  isMute: boolean;
  setIsMute: (mute: boolean) => void;
  isActive?: boolean;
};

const PLAY_ICON_SIZE = 102;
const PLAY_ICON_OFFSET = PLAY_ICON_SIZE / 2;

const QnVideoCard = ({
  bcVideo,
  isMute,
  setIsMute,
  isActive = false,
}: PlayerProps) => {
  const playerId = BC_PLAYER_ID_QN;
  const containerRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const toggleMute = useCallback(() => {
    setIsMute(!isMute);
  }, [isMute, setIsMute]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const playIconStyle = useMemo(
    () => ({
      width: PLAY_ICON_SIZE,
      height: PLAY_ICON_SIZE,
      left: `calc(50% - ${PLAY_ICON_OFFSET}px)`,
      top: `calc(50% - ${PLAY_ICON_OFFSET}px)`,
    }),
    [],
  );

  if (!bcVideo.id) return <></>;

  return (
    <div className="h-full w-full bg-transparent">
      {/* VIDEO CONTAINER */}
      <div className="relative mx-auto h-full w-full items-center justify-center overflow-hidden rounded-3xl">
        {/* Video Player */}
        {isActive && (
          <QnBrightcoveVideo
            videoId={bcVideo.id}
            forwardedRef={containerRef}
            className="quick-news-video !aspect-portrait"
            playerId={playerId}
            customButtons
            isMute={isMute}
            setIsMute={setIsMute}
            onPlay={handlePlay}
            onPause={handlePause}
          />
        )}

        {/* Poster Fallback */}
        <img
          src={bcVideo.poster}
          alt={bcVideo.name}
          loading="lazy"
          className={twMerge(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
            isActive && "opacity-0",
          )}
        />

        {/* Play Icon */}
        {!isPlaying && (
          <div
            className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 opacity-100"
            style={playIconStyle}
          >
            <img src={PlayIcon} alt="Play Video" />
          </div>
        )}
      </div>

      {/* TEXT + ACTIONS OVERLAY */}
      <div className="absolute bottom-0 z-30 w-full rounded-3xl bg-gradient-to-t from-black-100/60 via-black-100/30 to-transparent p-3">
        <Heading
          Element="h1"
          className="font-primary text-md font-semibold leading-650 text-red-500 lg:text-3md lg:leading-700"
        >
          <button
            onClick={toggleExpanded}
            className={twMerge(
              "w-full cursor-pointer text-left transition-all duration-300",
              expanded ? "line-clamp-none" : "line-clamp-2",
            )}
            title={`Tap to ${expanded ? "collapse" : "expand"}`}
          >
            {bcVideo.name}
          </button>
        </Heading>

        <div className="actions mt-auto flex items-start justify-between gap-2 px-2 py-2">
          {/* Mute/Unmute */}
          <button
            onClick={toggleMute}
            className="relative my-auto w-[30px] rounded"
          >
            <Image
              className="h-[17px] w-[25px]"
              src={isMute ? MuteButton : UnMuteButton}
              alt={isMute ? "Mute" : "Unmute"}
              srcWidth={22}
              srcHeight={17}
            />
          </button>

          {/* Social Share */}
          <div className="ml-auto flex items-end">
            <ShareSocialButton
              socialIconList={QN_SOCIAL_ICON_LIST}
              pagePath={`/quick-news/video/${bcVideo.id}`}
              mbPopupClassName="absolute right-0"
              quickNews={true}
              quickNewsLight={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

QnVideoCard.displayName = "QnVideoCard";

export default memo(QnVideoCard);
